import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import HomeView from "@/views/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import store from "@/store";
import { persistDeepLinkFromLocation } from "@/lib/deepLink";

function guard(to: any, from: any, next: any) {
  const rootState = store.state;
  const token = rootState.account.jwt;
  if (token) {
    next();
  } else {
    next({ path: "/auth0login", query: to.query });
  }
}

function basicUserGuard(to: any, from: any, next: any) {
  const rootState = store.state;
  const token = rootState.account.jwt;

  if (!token) {
    next({ path: "/auth0login", query: to.query });
    return;
  }

  const userType = rootState.notifyData?.current_user?.user_type;

  if (userType === "basic") {
    // Redirect basic users to chat list if they try to access restricted routes
    next("/chat-list");
  } else {
    next();
  }
}

export function propertyGuard(to, from, next) {
  const { current_user, redirectcompanyid, propertyList, redirectpropertyid, login_company_id } = store.state.notifyData;
  // current_user is empty on a cold start (nothing rehydrated from localStorage yet).
  // Dereferencing it directly throws inside the guard, which aborts navigation
  // silently and leaves a blank view.
  const userType = current_user?.user_type;
  if (userType === "master" && login_company_id === redirectcompanyid) {
    next();
  } else if (userType && userType !== "master" && (propertyList || []).some((property) => property.id === redirectpropertyid)) {
    next();
  } else {
    store.commit("RESET_REDIRECT_STATE");
    next("/property-list");
  }
}

export function combinedGuard(to, from, next) {
  guard(to, from, (guardNextArg) => {
    if (guardNextArg) {
      next(guardNextArg);
    } else {
      propertyGuard(to, from, next);
    }
  });
}

const routes: Array<RouteRecordRaw> = [
  // {
  //   path: "/",
  //   name: "home",
  //   component: HomeView,
  //   beforeEnter: (to: any, from: any, next: any) => {
  //     const rootState = store.state;
  //     const token = rootState.account.jwt;
  //     if (token) {
  //       next("/property-list");
  //     } else {
  //       next("/auth0login");
  //     }
  //   },
  // },
  {
    path: "/",
    name: "home",
    component: HomeView,
    beforeEnter: (to: any, from: any, next: any) => {
      const startTime = Date.now();
      const checkAuth = () => {
        const rootState = store.state;
        const token = rootState.account.jwt;
        const hasStoredState = localStorage.getItem("vuex");
        const elapsed = Date.now() - startTime;

        if (token) {
          // Check if token is expired
          try {
            const { jwtDecode } = require("jwt-decode");
            const decoded = jwtDecode(token);
            const isExpired = decoded.exp * 1000 < Date.now();
            const validMinutes = Math.round((decoded.exp * 1000 - Date.now()) / 1000 / 60);

            if (isExpired) {
              store.commit("RESET_APP_STATE");
              next({ path: "/auth0login", replace: true });
              return;
            }
          } catch (error) {
            // Token decode error - redirect to login
          }

          next({ path: "/property-list", replace: true });
        } else if (hasStoredState && elapsed < 400) {
          setTimeout(checkAuth, 50);
        } else {
          next({ path: "/auth0login", replace: true });
        }
      };
      checkAuth();
    },
  },
  {
    path: "/auth0login",
    name: "Login",
    // Email links hit /login?redirect*. A redirect-to-/auth0login on the
    // initial history entry drops that query (Vue Router resolves the
    // redirect before beforeEach can persist it). Alias keeps the URL
    // and the query on the same LoginView.
    alias: ["/login"],
    component: LoginView,
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import(/* webpackChunkName: "forgot-password" */ "@/views/ForgotPasswordView.vue"),
  },
  {
    path: "/Change_Password",
    name: "ResetPassword",
    component: () => import(/* webpackChunkName: "reset-password" */ "@/views/ResetPasswordView.vue"),
  },
  {
    path: "/reset-view",
    name: "ResetView",
    component: () => import(/* webpackChunkName: "reset-view" */ "@/views/ResetView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/property-list",
    name: "PropertyList",
    component: () => import(/* webpackChunkName: "property-list" */ "@/views/PropertyListView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/property-schedule",
    name: "PropertySchedule",
    component: () => import(/* webpackChunkName: "property-detail" */ "@/views/PropertyScheduleView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/add-team",
    name: "AddTeam",
    component: () => import(/* webpackChunkName: "add-team" */ "@/views/AddTeamView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/ico",
    name: "ICO",
    component: () => import(/* webpackChunkName: "ico" */ "@/views/ICOView.vue"),
    beforeEnter: combinedGuard,
  },
  {
    path: "/workorder",
    name: "WorkOrderMaster",
    component: () => import(/* webpackChunkName: "workorder" */ "@/views/WorkOrderMaster.vue"),
    beforeEnter: combinedGuard,
  },

  //Texting Routes
  {
    path: "/chat-list",
    name: "ChatList",
    component: () => import(/* webpackChunkName: "chat-list" */ "@/views/texting/ChatListView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/chat-detail",
    name: "ChatDetail",
    props: true,
    component: () => import(/* webpackChunkName: "chat-list" */ "@/views/texting/ChatDetailView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/optin-status",
    name: "OptinStatus",
    props: true,
    component: () => import(/* webpackChunkName: "optin-status" */ "@/views/texting/OptinStatusView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/user-profile",
    name: "UserProfile",
    component: () => import(/* webpackChunkName: "user-profile" */ "@/views/texting/UserProfileView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/mass-text-detail",
    name: "MassTextDetail",
    component: () => import(/* webpackChunkName: "mass-text-detail" */ "@/views/texting/MassTextDetailView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/new-text-message",
    name: "NewTextMessage",
    component: () => import(/* webpackChunkName: "new-text-message" */ "@/views/texting/NewTextMessageView.vue"),
    beforeEnter: basicUserGuard,
  },
  {
    path: "/group-text",
    name: "GroupText",
    component: () => import(/* webpackChunkName: "group-text" */ "@/views/texting/GroupTextView.vue"),
    beforeEnter: basicUserGuard,
  },
  {
    path: `/direct-chat/:propertyId/:channelId`,
    name: "MassText",
    component: () => {},
    beforeEnter: (to, from, next) => {
      const propertyId = to.params.propertyId;
      const channelId = to.params.channelId;
      store.commit("SET_ACTIVE_PROPERTY", null);
      store.commit("SET_ACTIVE_CHAT", null);
      store.commit("SET_DIRECT_CHAT_PARAMS", { propertyid: propertyId, channelId: channelId });
      next(`/chat-detail`);
    },
  },
  {
    path: "/call-activity",
    name: "CallActivity",
    component: () => import(/* webpackChunkName: "call-activity" */ "@/views/CallActivityView.vue"),
    beforeEnter: guard,
  },
  {
    path: "/all-unread-notifications",
    name: "AllUnreadNotifications",
    redirect: "/auth0login/all-unread-notifications",
  },
  {
    path: "/auth0login/all-unread-notifications",
    name: "AllUnreadNotificationsredirect",
    component: () => import(/* webpackChunkName: "login" */ "@/views/LoginView.vue"),
  },
  {
    path: "/unread-notifications/company/:companyId/property/:propertyId/channelid/:channelId",
    name: "UnreadNotificationsDetail",
    component: () => {},
    beforeEnter: (to, from, next) => {
      const propertyId = to.params.propertyId as string;
      const channelId = to.params.channelId as string;
      store.commit("SET_DIRECT_CHAT_PARAMS", { propertyid: propertyId, channelId: channelId });
      if (store.state.account.jwt) {
        next(`/direct-chat/${propertyId}/${channelId}`);
      } else {
        next(`/auth0login?propertyid=${encodeURIComponent(propertyId)}&channelId=${encodeURIComponent(channelId)}`);
      }
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Capture redirect* / chat query on every arrival, including /login before
// it rewrites to /auth0login. sessionStorage is what LoginView reads if
// the Auth0 callback or a string redirect strips the URL.
router.beforeEach((to, _from, next) => {
  persistDeepLinkFromLocation(to.query as Record<string, unknown>);
  next();
});

export default router;
